import React from 'react';

/**
 * A hover label for the chart's toolbar buttons.
 *
 * Those buttons carried native `title` attributes, which the browser will not show for a
 * control the pointer reaches by moving within the same element — and on the chart toolbar,
 * where the buttons sit shoulder to shoulder inside one absolutely-positioned bar, that is
 * most of the time. The native tooltip also waits about a second and renders in the OS
 * chrome, so on the darkened chart it read as a glitch even when it did appear.
 *
 * This is CSS-only: no state, no timers, no listeners per button.
 */
interface ChartTooltipProps {
  label: string;
  /** Which side of the control the label appears on. Defaults to below. */
  placement?: 'top' | 'bottom';
  children: React.ReactNode;
}

const ChartTooltip: React.FC<ChartTooltipProps> = ({ label, placement = 'bottom', children }) => (
  // Named group, so a tooltip cannot be triggered by hovering some outer group that happens
  // to wrap it — the toolbar has several.
  <span className="relative inline-flex group/charttip">
    {children}
    <span
      role="tooltip"
      className={`pointer-events-none absolute left-1/2 z-[60] -translate-x-1/2 whitespace-nowrap rounded px-2 py-1 text-[10px] font-medium text-white shadow-lg opacity-0 transition-opacity duration-150 group-hover/charttip:opacity-100 ${
        placement === 'bottom' ? 'top-full mt-1.5' : 'bottom-full mb-1.5'
      }`}
      style={{ backgroundColor: 'rgba(17, 24, 39, 0.95)' }}
    >
      {label}
    </span>
  </span>
);

export default ChartTooltip;
